import React, {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {Tooltip, OverlayTrigger} from 'react-bootstrap-v5'
import Button from '_core/components/Button'
import ConfirmationDialog from '_core/components/ConfirmationDialog/ConfirmationDialog'
import {useDispatch} from 'react-redux'
import {showMessage} from 'app/redux/messageSlice'
import customerService from 'app/services/CustomerService/CustomerService'

type Props = {
  customerId: string
  isCreate?: boolean
}

const CustomerDeleteButton: React.FC<Props> = ({
  customerId,
  isCreate,
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [canDeleteCustomerData, setCanDeleteCustomerData] = useState<{
    canDelete: boolean
    message: string
  } | null>(null)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  useEffect(() => {
    const validateCanDelete = async () => {
      if (customerId && !isCreate) {
        const data = await customerService.validateProspectCustomerDeletion(customerId)
        setCanDeleteCustomerData(data)
      }
    }
    validateCanDelete()
  }, [customerId, isCreate])

  const deleteCustomerConfirmationMessage = (
    <div>
      All customer records will be deleted.
      <br />
      This action cannot be undone.
      <br />
      Are you sure you want to delete this customer?
    </div>
  )

  const handleDeleteClick = () => {
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true)
      setDeleteError('')
      const result = await customerService.deleteProspectCustomer(customerId)
      if (result[0]?.deletedCount > 0) {
        dispatch(
          showMessage({
            text: 'Successfully deleted customer.',
            variant: 'success',
          })
        )
        navigate('/customers')
      } else {
        setDeleteError(result[0]?.message || 'An error occurred')
      }
    } catch (error) {
      setDeleteError('An unknown error occurred')
      console.error(error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteClose = () => {
    setShowDeleteDialog(false)
    setDeleteError('')
  }

  if (isCreate || !canDeleteCustomerData) {
    return null
  }
  return (
    <>
      <div className='me-5'>
        {!canDeleteCustomerData.canDelete ? (
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip id='tooltip'>
                {`Cannot delete customer. ${canDeleteCustomerData.message}`}
              </Tooltip>
            }
          >
            <span>
              <Button
                id='btnCustomerDelete'
                type='button'
                title='Delete'
                className='btn btn-secondary opacity-25'
                disabled={true}
              />
            </span>
          </OverlayTrigger>
        ) : (
          <Button
            id='btnCustomerDelete'
            type='button'
            title='Delete'
            className='btn btn-danger'
            onClick={handleDeleteClick}
          />
        )}
      </div>

      <ConfirmationDialog
        show={showDeleteDialog}
        title='Delete Customer'
        message={deleteCustomerConfirmationMessage}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        isSubmitting={isDeleting}
        errorMessage={deleteError}
        labelNo='Cancel'
        labelYes='Delete'
        confirmButtonClassName='btn-danger'
      />
    </>
  )
}

export default CustomerDeleteButton 